// ─────────────────────────────────────────────
// KLOTHIQ — Customer Form Page (wired)
// ─────────────────────────────────────────────

import { Input, Textarea } from '@/components/ui/FormControls';
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { optionalCustomerPhoneSchema } from '@klothiq/validation';
import PageHeader from '@/components/common/PageHeader';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import { useCustomer, useCreateCustomer, useUpdateCustomer } from '@/hooks/useCustomers';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const customerSchema = z.object({
  name:    z.string().min(2, 'Name must be at least 2 characters'),
  phone: optionalCustomerPhoneSchema,
  email:   z.string().email('Enter a valid email').or(z.literal('')).optional(),
  address: z.string().optional(),
  city:    z.string().optional(),
  pincode: z.string().regex(/^\d{6}$/, 'Pincode must be 6 digits').or(z.literal('')).optional(),
  notes:   z.string().optional(),
});

type CustomerFormValues = z.infer<typeof customerSchema>;

const inputCls = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50';

function Field({ id, label, error, required, children }: {
  id: string; label: string; error?: string; required?: boolean; children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}{required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

export default function CustomerFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const { data: existing, isLoading, error } = useCustomer(id);
  const create = useCreateCustomer();
  const update = useUpdateCustomer();

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<CustomerFormValues>({ resolver: zodResolver(customerSchema) });

  useEffect(() => {
    if (existing) {
      reset({
        name:    existing.name,
        phone:   existing.phone    ?? '',
        email:   existing.email    ?? '',
        address: existing.address  ?? '',
        city:    existing.city     ?? '',
        pincode: existing.pincode  ?? '',
        notes:   existing.notes    ?? '',
      });
    }
  }, [existing, reset]);

  const onSubmit = async (values: CustomerFormValues) => {
    try {
      if (isEditing) {
        await update.mutateAsync({ id, ...values });
        toast.success('Customer updated successfully');
      } else {
        await create.mutateAsync(values);
        toast.success('Customer created successfully');
      }
      navigate('/customers');
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof err.message === 'string'
          ? err.message
          : 'Customer could not be saved. Please try again.';
      toast.error(
        message.includes('customers_pincode_check')
          ? 'Enter a 6-digit pincode, or leave it blank.'
          : message,
      );
    }
  };

  if (isLoading && isEditing) return <LoadingState message="Loading customer…" />;
  if (error) return <ErrorState message={error.message} />;

  const isWorking = isSubmitting || create.isPending || update.isPending;

  return (
    <div>
      <PageHeader
        title={isEditing ? 'Edit Customer' : 'New Customer'}
        description={isEditing ? 'Update customer details' : 'Add a new customer'}
        backTo="/customers"
      />
      <div className="mx-auto max-w-lg">
        <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <Field id="customer-name" label="Name" required error={errors.name?.message}>
              <Input
                id="customer-name"
                {...register('name')}
                placeholder="Customer name"
                className={inputCls}
                aria-invalid={!!errors.name}
                helpLabel="Name"
              />
            </Field>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field id="customer-phone" label="Phone" error={errors.phone?.message}>
                <Input
                  id="customer-phone"
                  type="tel"
                  {...register('phone')}
                  placeholder="10-digit mobile"
                  maxLength={10}
                  className={inputCls}
                  helpLabel="Phone"
                />
              </Field>
              <Field id="customer-email" label="Email" error={errors.email?.message}>
                <Input
                  id="customer-email"
                  type="email"
                  {...register('email')}
                  placeholder="email@example.com"
                  className={inputCls}
                  helpLabel="Email"
                />
              </Field>
            </div>

            <Field id="customer-address" label="Address" error={errors.address?.message}>
              <Textarea
                id="customer-address"
                {...register('address')}
                rows={2}
                placeholder="Delivery address"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                helpLabel="Address"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field id="customer-city" label="City" error={errors.city?.message}>
                <Input
                  id="customer-city"
                  {...register('city')}
                  placeholder="City"
                  className={inputCls}
                  helpLabel="City"
                />
              </Field>
              <Field id="customer-pincode" label="Pincode" error={errors.pincode?.message}>
                <Input
                  id="customer-pincode"
                  {...register('pincode')}
                  placeholder="6 digits"
                  maxLength={6}
                  className={inputCls}
                  helpLabel="Pincode"
                />
              </Field>
            </div>

            <Field id="customer-notes" label="Notes" error={errors.notes?.message}>
              <Textarea
                id="customer-notes"
                {...register('notes')}
                rows={2}
                placeholder="Delivery instructions or other notes"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                helpLabel="Notes"
              />
            </Field>

            <div className="flex justify-end gap-3 border-t border-border pt-4">
              <button type="button" onClick={() => navigate('/customers')} className="rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-accent">Cancel</button>
              <button type="submit" disabled={isWorking} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
                {isWorking && <Loader2 className="h-4 w-4 animate-spin" />}
                {isEditing ? 'Update' : 'Create'} Customer
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
